import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismComboSelectInput,
  PrismSelectInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type { Task } from 'ember-concurrency';
import { t } from 'ember-intl';
import type { Hotel, Hotelinfo } from 'frontend-utils/models';
import {
  type RoomType,
  ROOMTYPES,
  type RoomtypeSelection,
} from 'frontend-utils/models/roomtype';
import {
  type Platform,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import registerObserver from 'frontend-utils/utils/register-observer';
import GeneralSelectionsMealPlan from 'lumen/components/general/selections/meal-plan';
import ParityDropdownsPosSelection from 'lumen/components/parity/dropdowns/pos-selection';
import type {
  SelectionsKey,
  SelectionsValue,
} from 'lumen/controllers/hotel/parity/list';
import type { ParityConfig } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  CUSTOM_ROOMTYPE_PREFIX,
  MEALTYPES,
  type MealTypeValues,
  type MembershipType,
  OTA_GROUPS,
  PLATFORM_TRANSLATION_KEYS,
  RATE_INSIGHT_FILTER_TYPE_RATE_PARAM,
  RATE_PARAMS,
  RATE_PARAMS_TO_TRANSLATION_KEYS,
  type RateParam,
  ROOMTYPE_TRANSLATION_KEYS,
} from 'lumen/utils/constants/general-constants';
import type {
  CountryCodeWithDefault,
  SelectInputOptionWithId,
} from 'lumen/utils/constants/pi-constant-values';
import { sortRoomtypeIds } from 'lumen/utils/sort-ids';

export const AVAILABLE_MEAL_PLANS = [
  MEALTYPES.ANY,
  MEALTYPES.ROOM_ONLY,
  MEALTYPES.BED_AND_BREAKFAST,
  MEALTYPES.HALF_BOARD,
  MEALTYPES.FULL_BOARD,
  MEALTYPES.ALL_IN,
];

export const AVAILABLE_PERSONS = [1, 2];

export const AVAILABLE_PLATFORMS = [
  PLATFORMS.PLATFORM_UNSPECIFIED,
  PLATFORMS.PLATFORM_MOBILE_WEBSITE,
  PLATFORMS.PLATFORM_MOBILE_APP,
];

interface ParitySelectionsSignature {
  Args: {
    config: Task<ParityConfig | null, []>;
    isLimitedParityView?: boolean;
    isRoomtypeRequestLoading?: boolean;
    rateParam: RateParam;
    selectedLos: number;
    selectedMaxPersons: number;
    selectedMealPlan: MealTypeValues;
    selectedMembershipType: MembershipType;
    selectedOTAGroupId: string;
    selectedOTAId: string;
    selectedPlatform: Platform;
    selectedPos: CountryCodeWithDefault;
    selectedRoomType: RoomType;
    selectionsChanged: (
      selection: SelectionsKey,
      value: SelectionsValue,
    ) => void;
    showParityAnalyticsData: boolean;
  };
}

export default class ParitySelections extends Component<ParitySelectionsSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  get availableLos(): SelectInputOptionWithId<number>[] {
    let options = this.args.showParityAnalyticsData
      ? this.hotel.defaultLosParityAnalytics
      : this.hotel.defaultLosParity;

    if (
      !this.args.showParityAnalyticsData &&
      this.access.hasParityCustomLiveupdate
    ) {
      const shoppedLosValues = [1, 2, 3, 4, 5, 6, 7];
      options = [...new Set([...shoppedLosValues, ...options])];
    }

    return options
      .toSorted((a, b) => a - b)
      .map((los) => ({
        id: los,
        label: this.intl.t('rates.nights', { count: los }),
      }));
  }

  get availableOTAGroups(): SelectInputOptionWithId<string>[] {
    return [
      { label: this.intl.t('general.major_otas'), id: OTA_GROUPS.MAJOR },
      { label: this.intl.t('general.all_otas'), id: OTA_GROUPS.ALL },
    ];
  }

  get availablePlatforms(): SelectInputOptionWithId<Platform>[] {
    return AVAILABLE_PLATFORMS.map((platform) => ({
      id: platform,
      label: this.intl.t(PLATFORM_TRANSLATION_KEYS[platform]),
    }));
  }

  @dependentKeyCompat
  get availableRoomTypes(): SelectInputOptionWithId<RoomType>[] {
    if (this.args.isRoomtypeRequestLoading) {
      return [];
    }

    const output: SelectInputOptionWithId<RoomType>[] = [];
    this.getRoomtypes().forEach((x) => {
      if (x.id && x.name) {
        output.push({
          id: x.id,
          label: x.id.startsWith(CUSTOM_ROOMTYPE_PREFIX)
            ? x.name // Custom roomtypes have their name as label
            : this.intl.t(ROOMTYPE_TRANSLATION_KEYS[x.id]),
        });
      }
    });

    output.sort((a, b) => sortRoomtypeIds(a.id, b.id));
    output.unshift({
      label: this.intl.t(ROOMTYPE_TRANSLATION_KEYS[ROOMTYPES.ALL]),
      id: ROOMTYPES.ALL,
    });
    return output;
  }

  get defaultLosId(): number {
    return this.availableLos[0]!.id;
  }

  get hasFailedFetchingConfig(): boolean {
    return this.args.config.last?.isError ?? false;
  }

  get hasLosFilter(): boolean {
    return this.availableLos.length > 1;
  }

  get hotel(): Hotel {
    return this.hotelAndMarketService.hotel;
  }

  get isFetchingConfig(): boolean {
    return this.args.config.isRunning;
  }

  get maxPersonsOptions(): SelectInputOptionWithId<number>[] {
    return AVAILABLE_PERSONS.map((maxPersonsOption) => ({
      id: maxPersonsOption,
      label: this.intl.t('rates.persons_selection', {
        count: maxPersonsOption,
      }),
    }));
  }

  get ownHotelinfo(): Hotelinfo {
    return this.hotel.hotelinfo;
  }

  get posValues(): CountryCodeWithDefault[] {
    if (!this.args.config.last?.value) {
      return [];
    }

    return this.args.config.last.value.posValues ?? [];
  }

  get rateParams(): SelectInputOptionWithId<RateParam>[] {
    const rateParams: SelectInputOptionWithId<RateParam>[] = [
      {
        label: this.intl.t(
          RATE_PARAMS_TO_TRANSLATION_KEYS[RATE_PARAMS.BEST_FLEX],
        ),
        icon: 'rates-best-flex',
        id: RATE_PARAMS.BEST_FLEX,
      },
      {
        label: this.intl.t(RATE_PARAMS_TO_TRANSLATION_KEYS[RATE_PARAMS.LOWEST]),
        icon: 'rates-lowest',
        id: RATE_PARAMS.LOWEST,
      },
    ];

    // Check if at least one of the supported OTAs has 3rd party rates
    if (this.hotel.OTAsParity.some((ota) => ota.hasThirdPartyFilter)) {
      rateParams.push({
        label: this.intl.t(
          RATE_PARAMS_TO_TRANSLATION_KEYS[RATE_PARAMS.THIRD_PARTY],
        ),
        icon: 'rates-third-party',
        id: RATE_PARAMS.THIRD_PARTY,
        type: RATE_INSIGHT_FILTER_TYPE_RATE_PARAM,
      });
    }

    return rateParams;
  }

  get selectedLosValue(): SelectInputOptionWithId<number> {
    return this.availableLos.find((los) => los.id === this.args.selectedLos)!;
  }

  get selectedMaxPersonsValue(): SelectInputOptionWithId<number> {
    return this.maxPersonsOptions.find(
      (option) => option.id === this.args.selectedMaxPersons,
    )!;
  }

  get selectedOTAGroup(): SelectInputOptionWithId<string> {
    return this.availableOTAGroups.find(
      (OTAGroup) => OTAGroup.id === this.args.selectedOTAGroupId,
    )!;
  }

  get selectedPlatformItem(): SelectInputOptionWithId<Platform> {
    return this.availablePlatforms.find(
      (platform) => platform.id === this.args.selectedPlatform,
    )!;
  }

  get selectedRateType(): SelectInputOptionWithId<RateParam> {
    return this.rateParams.find(
      (rateParam) => rateParam.id === this.args.rateParam,
    )!;
  }

  get selectedRoomTypeItem(): SelectInputOptionWithId<RoomType> {
    const selectedRoomType = this.availableRoomTypes?.find(
      (roomType) => roomType.id === this.args.selectedRoomType,
    );

    return (
      selectedRoomType ?? {
        id: ROOMTYPES.ALL,
        label: this.intl.t(ROOMTYPE_TRANSLATION_KEYS[ROOMTYPES.ALL]),
      }
    );
  }

  constructor(owner: Owner, args: ParitySelectionsSignature['Args']) {
    super(owner, args);

    // eslint-disable-next-line @typescript-eslint/unbound-method -- registerObserver binds it to this
    registerObserver(this, ['availableRoomTypes.[]'], this.roomtypeObserver);
  }

  // Merge roomtypeSelections of all roomtypes in the store that belong to the active compset
  getRoomtypes(): RoomtypeSelection[] {
    // First try to fetch the roomtypes for the own hotelinfo
    const roomtypesForOwnHotelInfo = this.ownHotelinfo?.getRoomTypesForOTA(
      this.args.selectedOTAId,
    );

    if (roomtypesForOwnHotelInfo.length) {
      return roomtypesForOwnHotelInfo;
    }

    const uniqueRoomtypeSelections: RoomtypeSelection[] = [];
    const roomtypeSelectionsIds = new Set();

    // In case the own hotelinfo has no roomtypes (e.g. pre-opening hotel), fetch the roomtypes of the competitors instead
    this.hotelAndMarketService.primaryAndSecondaryCompetitors.forEach(
      (hotelinfo) => {
        hotelinfo.getRoomTypesForOTA(this.args.selectedOTAId).forEach((rt) => {
          if (!roomtypeSelectionsIds.has(rt.id)) {
            roomtypeSelectionsIds.add(rt.id);
            uniqueRoomtypeSelections.push(rt);
          }
        });
      },
    );

    return uniqueRoomtypeSelections;
  }

  roomtypeObserver(): void {
    if (this.availableRoomTypes && this.availableRoomTypes?.length > 0) {
      // Clear the room type filter if the (custom) room type is not available for the current selection
      if (
        !this.availableRoomTypes.find(
          (roomType) => roomType.id === this.args.selectedRoomType,
        )
      ) {
        this.args.selectionsChanged('roomType', ROOMTYPES.ALL);
      }
    }
  }

  @action
  selectionsChanged(
    queryParam: SelectionsKey,
    value: SelectInputOptionWithId<SelectionsValue>,
  ): void {
    this.args.selectionsChanged(queryParam, `${value.id}`);
  }

  <template>
    <div class="rates-navbar flex flex-row justify-between">
      <div class="rates-navbar__selections flex items-center justify-center">
        <div class="ota-selections-container rates-navbar-scroller gap-200">
          <PrismSelectInput
            @onChange={{fn this.selectionsChanged "rateParam"}}
            @options={{this.rateParams}}
            @value={{this.selectedRateType}}
          />

          <PrismSelectInput
            @onChange={{fn this.selectionsChanged "platform"}}
            @options={{this.availablePlatforms}}
            @value={{this.selectedPlatformItem}}
          />

          {{#if this.hasLosFilter}}
            <PrismSelectInput
              @onChange={{fn this.selectionsChanged "los"}}
              @options={{this.availableLos}}
              @value={{this.selectedLosValue}}
              data-test-id="los-select"
            />
          {{else}}
            <div class="no-filter-content" data-test-id="los-static">
              {{t "rates.nights" count=this.defaultLosId}}
            </div>
          {{/if}}

          <PrismSelectInput
            @onChange={{fn this.selectionsChanged "maxPersons"}}
            @options={{this.maxPersonsOptions}}
            @value={{this.selectedMaxPersonsValue}}
          />

          <span>
            <PrismComboSelectInput
              @disabled={{@isRoomtypeRequestLoading}}
              @onChange={{fn this.selectionsChanged "roomType"}}
              @options={{this.availableRoomTypes}}
              @unsafe_dropdownClass="room-type-select-dropdown--max-width"
              @value={{this.selectedRoomTypeItem}}
            />
            {{#if @isRoomtypeRequestLoading}}
              <PrismTooltip
                @side="bottom"
                @text={{t "general.filter_loading"}}
              />
            {{/if}}
          </span>

          <GeneralSelectionsMealPlan
            @allMealPlans={{AVAILABLE_MEAL_PLANS}}
            @onChange={{fn @selectionsChanged "mealPlan"}}
            @selectedMealPlan={{@selectedMealPlan}}
          />

          <ParityDropdownsPosSelection
            @hasError={{this.hasFailedFetchingConfig}}
            @isLoading={{this.isFetchingConfig}}
            @onChange={{fn this.selectionsChanged "pos"}}
            @posValues={{this.posValues}}
            @selectedPos={{@selectedPos}}
          />

          {{#if @isLimitedParityView}}
            <PrismSelectInput
              @onChange={{fn this.selectionsChanged "OTAGroup"}}
              @options={{this.availableOTAGroups}}
              @value={{this.selectedOTAGroup}}
            />
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
